/* eslint-disable array-callback-return */
import {
  useBoolean,
  useToastr,
} from '@laxmimanogna/code-quick-components';
import { createContext, useEffect, useState } from 'react';
import { successToast } from '../components/successToast';
import { ROLES_VALUES } from '../constants/constants';
import { REBUTTAL_STATUS, RESOLVED_STATUS } from '../pages/audit_sheet/constants';
import auditSheetRepository from '../repositories/AuditSheetRepository';
import { toTitleCase } from '../utils/common.utils';
import { createEmptyTablesProps } from '../pages/audit_sheet/utils/commonAuditSheet.utils';

export const AuditSheetContext = createContext({
  isAuditSheetLoading: true,
  createAuditSheet: async () => {},
  getAuditSheet: async () => {},
  monitorAuditHour: async () => {},
  currentUpload: {},
  getAuditUpload: async () => {},
  updateAuditSheet: async () => {},
  getIndustryCodes: async () => {},
  getICDCodes: async () => {},
  getModifiers: async () => {},
  industryCodes: {},
  iCDCodes: {},
  modifiers: {},
  recentAuditImageUpload: async () => {},
  updateActivity: async () => {},
  getUsers: async () => {},
  getAllUsers: async () => {},
  getComments: async () => {},
  fetchComments: async () => [],
  comments: [],
  setComments: async () => {},
  users: [],
  allUsers: [],
  isUpdateActivityLoading: false,
  isUserLoading: false,
  isCommentLoading: false,
  allComments: [],
  setAllComments: () => {},
  getAllComments: async () => {},
  commentFilters: {},
  setCommentFilters: () => {},
  getCommentFlag: async () => {},
  commentFlags: {},
  setCommentFlags: () => {},
  selectedCell: {},
  setSelectedCell: () => {},
  deleteComment: async () => {},
  updateComment: async () => {},
  postComment: async () => {},
  isPostCommentLoading: false,
  isAllCommentLoading: false,
  totalComments: 0,
  providerOptions: [],
  fetchProviderDropdown: async () => {},
  responseAuditIssues: [],
  fetchResponseAuditIssues: async () => {},
  sendToAuditor: async () => {},
  getAvailableAuditors: async () => [],
  getRebuttalSubmitValidation: async () => {},

  // batch audit sheet
  getBatchAuditSheet: async () => {},
  isAccessDenied: true,
  getFileObjectByBatchId: async () => {},
  getBatchCharts: async () => {},
  isFileLoading: false,
});
const AuditSheetProvider = props => {
  const toast = useToastr();

  const [isAuditSheetLoading, iasState] = useBoolean(true);
  const [currentUpload, setCurrentUpload] = useState({});
  const [industryCodes, setIndustryCode] = useState({});
  const [iCDCodes, setICDCode] = useState({});
  const [modifiers, setModifiers] = useState({});
  const [users, setUsers] = useState([]);
  const [isUpdateActivityLoading, iuaState] = useBoolean(false);
  const [isCommentLoading, icState] = useBoolean(false);
  const [isUserLoading, iuState] = useBoolean(false);
  const [allUsers, setAllUsers] = useState([]);
  const [isPostCommentLoading, ipclState] = useBoolean(false);
  const [comments, setComments] = useState([]);
  const [allComments, setAllComments] = useState([]);

  // audit state version to resolve racing issues
  const [auditSheetVersion, setAuditSheetVersion] = useState(0);
  const [isAllCommentLoading, iaclState] = useBoolean(false);

  const [commentFlags, setCommentFlags] = useState({});

  const [selectedCell, setSelectedCell] = useState({});
  const [commentFilters, setCommentFilters] = useState({});
  const [totalComments, setTotalComments] = useState(0);
  const [providerOptions, setProviderOptions] = useState([]);
  const [responseAuditIssues, setResponseAuditIssues] = useState([]);
  const [sheetData, setSheetData] = useState({});
  const [chartBatchId, setChartBatchId] = useState(null);
  const [isFileLoading, iflState] = useBoolean(false);
  const [isAccessDenied, setAccessDeniedState] = useBoolean(true);


  async function createAuditSheet(payload, uploadId) {
    try {
      const response = await auditSheetRepository.createAuditSheet(
        {version: auditSheetVersion,
        sheets: payload},
        uploadId
      );
      return response;
    } catch (err) {
      toast.showError({
        description: err.toString(),
        duration: 10000
      });
    }
  }
  async function updateAuditSheet(payload, uploadId) {
    try {
      iasState.on();
      const response = await auditSheetRepository.updateAuditSheet(
        {version: auditSheetVersion,
        sheets: payload},
        uploadId
      );
      return response;
    } catch (err) {
      toast.showError({
        description: err.toString(),
        duration: 10000
      });
    } finally {
      iasState.off();
    }
  }

  async function getAuditSheet(uploadId, currentUpload) {
    try {
      iasState.on();
      const response = await auditSheetRepository.getAuditSheet(uploadId);
      setAccessDeniedState.off();

      if (!response.sheets.length) {
        return createEmptyTablesProps(currentUpload, currentUpload);
      }

      setAuditSheetVersion(response.version)
      return response.sheets;
    } catch (err) {
      const status = err?.status;
      if(status === 404 || status === 403){
        setAccessDeniedState.on();
      }
      else{
        setAccessDeniedState.off();
        toast.showError({
          description: err.toString(),
        });
      }
      return {"error": err?.cause?.detail};
    } finally {
      iasState.off();
    }
  }

  async function getAuditUpload(uploadId) {
    try {
      iasState.on();
      iflState.on();
      const response = await auditSheetRepository.getAuditUpload(uploadId);
      setAccessDeniedState.off();
      if (Object.keys(response).length) {
        setCurrentUpload(response);
      }
      return response;
    } catch (err) {
      iasState.off();
      const status = err?.status;
      if(status === 404 || status === 403){
        setAccessDeniedState.on();
      } else {
        setAccessDeniedState.off();
        toast.showError({
          description: err.toString(),
        });
      }
      iasState.off();
      return {"error": err?.cause?.detail};
    }
    finally {
      iflState.off();
    }
  }

  /**
   * Charts sitting under the same batch, used by the audit sheet navigator.
   *
   * Returns null (instead of toasting) whenever the batch cannot be resolved
   * with certainty, so the navigator stays hidden rather than offering
   * navigation we are not sure about. The exact-match check is kept even though
   * the request now filters on batch_id server side -- if that ever stops being
   * honoured we want the navigator to disappear, not to list another batch's
   * charts.
   */
  async function getBatchCharts(batchId) {
    if (!batchId) return null;
    try {
      const response = await auditSheetRepository.getBatchCharts(batchId);
      const batchRow = (response?.results || []).find(
        row => String(row.upload_id) === String(batchId)
      );
      if (!batchRow?.subRows?.length) return null;

      return {
        batchId: batchRow.upload_id,
        batchName: batchRow.name,
        charts: [...batchRow.subRows].sort((a, b) => a.id - b.id),
      };
    } catch (err) {
      return null;
    }
  }

  const getFileObjectByBatchId = async (batchId, rendererId = null) => {
    try {
      iflState.on();
      let queryParams = null
      if (rendererId) {
        queryParams = {renderer_id: rendererId};
      }
      const response = await auditSheetRepository.getFileObjectByBatchId(
        batchId,
        queryParams
      );
      return response;
    } catch (err) {
      toast.showError({
        description: err.toString(),
        description: `${err.toString()}.`,
      });
    } finally {
      iflState.off();
    }
  }

  async function getBatchAuditSheet(batchId, rendererId) {
    try {
      iasState.on()
      const QUERY_PARAMS = (rendererId) ? {'renderer_id': rendererId} : null;
      const response = await auditSheetRepository.getBatchAuditSheet(batchId, QUERY_PARAMS);
      setAccessDeniedState.off();
      if (!response.length) {
        return [{...createEmptyTablesProps()[0], batch_name: response?.batch_name}];
      }
      return response;
    } catch (err) {
      const status = err?.status;
      if(status === 404 || status === 403){
        setAccessDeniedState.on();
      } else{
        setAccessDeniedState.off();
        toast.showError({
          description: err.toString(),
        });
      }
      return {error: err?.cause?.detail};
    } finally {
      iasState.off();
    }
  }

  async function monitorAuditHour(uploadId) {
    try {
      await auditSheetRepository.auditHourMonitor(uploadId);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
      return {"error": err.cause.detail};
    }
  }

  async function recentAuditImageUpload(uploadId, data) {
    try {
      await auditSheetRepository.recentAuditImageUpload(uploadId, data);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
      return {"error" : err.cause.detail};
    }
  }

  async function getIndustryCodes(uploadId) {
    try {
      const response = await auditSheetRepository.getIndustryCodes();
      setIndustryCode(response);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    }
  }

  async function getICDCodes(uploadId) {
    try {
      const response = await auditSheetRepository.getICDCodes();
      setICDCode(response);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    }
  }

  async function getModifiers(uploadId) {
    try {
      const response = await auditSheetRepository.getModifiers();
      setModifiers(response);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    }
  }

  async function updateActivity(data) {
    try {
      iuaState.on();
      const response = await auditSheetRepository.updateActivity(data);
      if (data.length) {
        const updatedFlags = { ...commentFlags };
        data[0].row.map(rowId => {
          updatedFlags[rowId] = { ...(commentFlags[rowId] || {}) };
          data[0].column.forEach(col => { updatedFlags[rowId][col] = true; });
        });
        setCommentFlags(updatedFlags);
      }
      const toastProps = {
        header: 'Success!',
        description: `${
          data[0].action === REBUTTAL_STATUS
            ? 'Marked as rebuttal.'
            : !data[0].parent
            ? 'Comment sent'
            : 'Reply sent'
        }`,
      };
      successToast(toast, toastProps);
      return response;
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    } finally {
      iuaState.off();
    }
  }

  async function getUsers() {
    try {
      iuState.on();
      const response = await auditSheetRepository.getUsers();
      const userData = response.map(r => {
        return {
          id: r.id,
          display: `${r.first_name} ${r.last_name} | ${ROLES_VALUES[r.role]}`,
          first_name: r.first_name,
          last_name: r.last_name,
        };
      });
      setUsers([...userData]);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    } finally {
      iuState.off();
    }
  }

  async function getAllUsers() {
    try {
      iuState.on();
      const response = await auditSheetRepository.getAllUsers();
      const userData = response.map(r => {
        return {
          id: r.id,
          first_name: r.first_name,
          last_name: r.last_name,
        };
      });
      setAllUsers([...userData]);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    } finally {
      iuState.off();
    }
  }

  async function getComments(queryParams) {
    try {
      icState.on();
      const response = await auditSheetRepository.getComments(queryParams);
      // return response
      setComments(response.results ?? []);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
      return {"error": err?.cause?.detail};
    } finally {
      icState.off();
    }
  }

  async function fetchComments(uploadId, queryParams) {
    try {
      const response = await auditSheetRepository.getComments(uploadId, queryParams);
      return response.results || [];
    } catch (err) {
      return [];
    }
  }

  async function getAllComments() {
    try {
      iaclState.on();
      const { uploadId, batchId, ...queryParams } = commentFilters;
        let response;
        if (!uploadId && batchId) {
          response = await auditSheetRepository.getBatchAllComments(batchId, queryParams);
        } else {
          response = await auditSheetRepository.getComments(commentFilters);
        }
        let results = response.results || [];
        let totalCount = response.count || 0;

        // In single chart view, also fetch batch-level notes for the batch this chart belongs to.
        // Derive the batch ID from currentUpload (batch_id field) or fall back to any
        // comment that already carries a batch reference.
        if (commentFilters.uploadId && !commentFilters.batchId) {
          const derivedBatchId =
            currentUpload?.batch_id ||
            currentUpload?.batch ||
            chartBatchId ||
            results.find(c => c.batch)?.batch;
            if (derivedBatchId) {
            const batchResp = await auditSheetRepository.getComments({
              batchId: derivedBatchId,
              reverse: commentFilters.reverse,
              page: 1,
            }).catch(() => ({ results: [] }));
            if (batchResp.results?.length) {
              const existingIds = new Set(results.map(c => c.id));
              const newItems = batchResp.results.filter(c => !existingIds.has(c.id));
              results = [...results, ...newItems];
              // Do NOT add to totalCount — supplementary fetch should not affect pagination.
            }
          }
        }

        if (response.results) {
          setTotalComments(totalCount);
          if (commentFilters.page === 1) {
            setAllComments(results);
          }
          return results;
        }
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
      return {"error": err.cause?.detail}
    } finally {
      iaclState.off();
    }
  }

  async function getCommentFlag({ uploadId, batchId }) {
    try {
      const response = await auditSheetRepository.getCommentFlag({
        uploadId,
        batchId,
      });
      const obj = {};
      if (response.length) {
        for (let i = 0; i < response.length; i++) {
          const comment = response[i];
          const rowId = comment[0];
          const colName = comment[1][0];
          const status = comment[1][1];
          const isOpen = !status || !RESOLVED_STATUS.includes(status);
          if (!obj[rowId]) obj[rowId] = {};
          obj[rowId][colName] = isOpen;
        }
      }
      setCommentFlags(obj);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
      return {"error": err.cause.detail};
    }
  }

  async function deleteComment(commentId, commentParentId, notes) {
    try {
      await auditSheetRepository.deleteComment(commentId);
      if (commentParentId) {
        setAllComments(prev => prev.map(c =>
          c.id === commentParentId
            ? { ...c, reply_comments: (c.reply_comments || []).filter(r => r.id !== commentId) }
            : c
        ));
      } else {
        setAllComments(prev => prev.filter(c => c.id !== commentId));
      }
      const toastProps = {
        header: 'Success!',
        description: `${notes ? 'Note' : 'Comment'} is deleted`,
      };
      successToast(toast, toastProps);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    }
  }

  async function updateComment(commentId, payload, commentParentId) {
    try {
      payload.action = 'UPDATE';
      await auditSheetRepository.editComment(commentId, payload);
      if (commentParentId) {
        setAllComments(prev => prev.map(c =>
          c.id === commentParentId
            ? {
                ...c,
                reply_comments: (c.reply_comments || []).map(r =>
                  r.id === commentId ? { ...r, comment: payload.comment } : r
                ),
              }
            : c
        ));
      } else {
        setAllComments(prev => prev.map(c =>
          c.id === commentId ? { ...c, comment: payload.comment } : c
        ));
      }
      const toastProps = {
        header: 'Success!',
        description: `${payload.notes ? 'Note' : 'Comment'} is updated`,
      };
      successToast(toast, toastProps);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    }
  }

  async function postComment(data) {
    try {
      ipclState.on();
      if (!data.tagged_user) {
        data.tagged_user = [];
      }
      const response = await auditSheetRepository.postComment(data);
      return response;
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    } finally {
      ipclState.off();
    }
  }

  async function fetchProviderDropdown(uploadId) {
    try {
      const response = await auditSheetRepository.getProviderDropdown(uploadId);
      setProviderOptions(
        response.map(p => ({
          id: p.provider_id,
          value: toTitleCase(`${p.first_name} ${p.last_name}`),
        }))
      );
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
      return {"error": err.cause.detail};
    }
  }

  async function fetchResponseAuditIssues(uploadId) {
    try {
      const response = await auditSheetRepository.getResponseAuditIssues(uploadId);
      setResponseAuditIssues(response);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    }
  }

  async function getAvailableAuditors(specialtyId) {
    try {
      return await auditSheetRepository.getAvailableAuditors(specialtyId);
    } catch (err) {
      toast.showError({ description: err.toString() });
      return [];
    }
  }

  async function sendToAuditor(uploadId, auditorId) {
    try {
      const response = await auditSheetRepository.sendToAuditor(uploadId, auditorId);
      if (response?.message) {
        setCurrentUpload(prev => ({ ...prev, status: 'REBUTTAL IN PROGRESS' }));
        successToast(toast, { header: 'Success!', description: 'Chart sent to auditor for correction.' });
      }
      return response;
    } catch (err) {
      toast.showError({ description: err.toString() });
    }
  }

  async function getRebuttalSubmitValidation(uploadId) {
    try {
      const response = await auditSheetRepository.getRebuttalSubmitValidation(uploadId);
      return response;
    } catch (err) {
      toast.showError({ description: err.toString() });
    }
  }

  const mContext = {
    isAuditSheetLoading,
    createAuditSheet,
    getAuditSheet,
    monitorAuditHour,
    currentUpload,
    getAuditUpload,
    updateAuditSheet,
    getIndustryCodes,
    getICDCodes,
    getModifiers,
    industryCodes,
    iCDCodes,
    modifiers,
    recentAuditImageUpload,
    updateActivity,
    getUsers,
    getAllUsers,
    getComments,
    fetchComments,
    comments,
    setComments,
    users,
    allUsers,
    isUpdateActivityLoading,
    isUserLoading,
    isCommentLoading,
    allComments,
    setAllComments,
    getAllComments,
    commentFilters,
    setCommentFilters,
    getCommentFlag,
    commentFlags,
    setCommentFlags,
    selectedCell,
    setSelectedCell,
    deleteComment,
    updateComment,
    postComment,
    isPostCommentLoading,
    isAllCommentLoading,
    totalComments,
    providerOptions,
    fetchProviderDropdown,
    responseAuditIssues,
    fetchResponseAuditIssues,
    sendToAuditor,
    getAvailableAuditors,
    getRebuttalSubmitValidation,
    sheetData,
    setSheetData,
    setCurrentUpload,
    chartBatchId,
    setChartBatchId,

    getFileObjectByBatchId,
    getBatchCharts,
    getBatchAuditSheet,
    isAccessDenied,
    isFileLoading,
  };

  return (
    <AuditSheetContext.Provider value={mContext}>
      {props.children}
    </AuditSheetContext.Provider>
  );
};

export default AuditSheetProvider;
